import Icon from 'components/_UI/Icon';
import React from 'react'
import { AlignLeft, Calendar, CheckSquare, Clock, Hash, Trash2, Upload } from 'lucide-react';
import styled from 'styled-components'
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import customFieldsTypes from 'theme/customFieldsTypes';
import fonts from 'theme/fonts';

const typeIconMap = {
    text: (c) => <Icon name='short-text' color={c} />,
    textarea: (c) => <AlignLeft color={c} size={14} />,
    numeric: (c) => <Hash color={c} size={14} />,
    date: (c) => <Calendar color={c} size={14} />,
    time: (c) => <Clock color={c} size={14} />,
    checkbox: (c) => <CheckSquare color={c} size={14} />,
    upload: (c) => <Upload color={c} size={14} />,
    radio: (c) => <Icon name='radio-checked' color={c} />,
};

export default function CustomFieldCard({ data, onDelete }) {
    const selectedType = customFieldsTypes.find(el => data?.type === el?.value);
    const iconRenderer = typeIconMap[data?.type];

    return (
        <Card>
            <CardTop>
                <TypeBadge>
                    <TypeIconWrap>
                        {iconRenderer
                            ? iconRenderer(colors.occasionalPurple)
                            : null
                        }
                    </TypeIconWrap>
                    <TypeLabel>{selectedType?.label || data?.type}</TypeLabel>
                </TypeBadge>

                {onDelete && (
                    <DeleteBtn
                        onClick={(e) => { e.stopPropagation(); onDelete(data); }}
                        aria-label={`Excluir campo ${data?.label}`}
                    >
                        <Trash2 size={14} color={colors.danger} />
                    </DeleteBtn>
                )}
            </CardTop>

            <FieldName>{data?.label}</FieldName>

            <MetaRow>
                {data?.required ? (
                    <RequiredPill>Obrigatório</RequiredPill>
                ) : (
                    <OptionalPill>Opcional</OptionalPill>
                )}
            </MetaRow>

            {!!data?.options?.length && (
                <OptionsSection>
                    <OptionsLabel>Opções de escolha</OptionsLabel>
                    <OptionsWrap>
                        {data.options.map((el, i) => (
                            <OptionTag key={i}>{el}</OptionTag>
                        ))}
                    </OptionsWrap>
                </OptionsSection>
            )}
        </Card>
    )
}

const Card = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 18px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 6px ${colors.night}08;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
    display: flex;
    flex-direction: column;

    &:hover {
        box-shadow: 0 3px 12px ${colors.night}10;
        border-color: ${colors.night}22;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 24%;
        margin-right: 1%;
    }
`;

const CardTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const TypeBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 4px;
    background: ${colors.occasionalPurple}0C;
    border-radius: 8px;
`;

const TypeIconWrap = styled.div`
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: ${colors.occasionalPurple}15;
    display: flex;
    align-items: center;
    justify-content: center;

    svg, div {
        width: 14px;
        height: 14px;
    }
`;

const TypeLabel = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.occasionalPurple};
    white-space: nowrap;
`;

const DeleteBtn = styled.div`
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 8px;
    border: 1px solid transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
    opacity: 0.5;

    &:hover {
        opacity: 1;
        background: ${colors.danger}0A;
        border-color: ${colors.danger}44;
    }
`;

const FieldName = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
    line-height: 1.3;
    margin-bottom: 8px;
    word-break: break-word;
`;

const MetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
`;

const RequiredPill = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.danger};
    background: ${colors.danger}0C;
    padding: 3px 10px;
    border-radius: 20px;
`;

const OptionalPill = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.night};
    background: ${colors.night}0C;
    padding: 3px 10px;
    border-radius: 20px;
`;

const OptionsSection = styled.div`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${colors.night}0D;
`;

const OptionsLabel = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 8px;
`;

const OptionsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const OptionTag = styled.span`
    padding: 4px 10px;
    background: ${colors.secondaryPurple}0C;
    color: ${colors.secondaryPurple};
    border-radius: 6px;
    font-size: ${fonts.sizes.mini};
    font-weight: 500;
`;
